import {
  _Boolean,
  Product,
  ModelFavoriteUserFavoritesCompositeKeyConditionInput,
  ModelIntKeyConditionInput,
  Language,
  AnnouncementSetting,
} from '@/API';
import { MenuType } from '@/appsync/graphql/menu';
import {
  GetMenusQueryVariables,
  GetMenusQuery,
  CategoryIdsCollection,
  getMenusByIds,
} from '@/appsync/graphql/menu';
import { FavoriteType } from '@/appsync/graphql/user';
import {
  LoginUserAllInfoQuery,
  LoginUserAllInfoQueryVariables,
  LoginUserAllInfoWithoutAnnouncementQuery,
  LoginUserAllInfoWithoutAnnouncementQueryVariables,
  LoginUserAllInfoWithoutCWSQuery,
  getLoginUserAllInfo,
  getLoginUserAllInfoWithoutAnnouncement,
  getLoginUserAllInfoWithoutCWS,
} from '@/appsync/graphql/user';
import { UserAnnouncementType } from '@/appsync/graphql/userAnnouncement';
import {
  graphqlQuery,
  getNameByLang,
  getDescriptionByLang,
  getIcon,
  addParameterToUrl,
} from '@/appsync/service/GraphqlUtils';
import { favoriteListModule } from '@/store/modules/FavoriteListStore';
import {
  ID,
  UserInfo,
  ProductMenuMap,
  TenantLandscape,
  ProductAdditionalQueryMap,
  LoginInfo,
  LoginUserAllInfo,
  UserAnnouncement,
} from '@/types/type';
import {
  getTimeOfToday,
  getTimeOfTomorrow,
  convertUserAnnouncement,
} from '@/utils/AnnouncementUtils';
import { getSHA3of, _BooleanEnum2boolean } from '@/utils/CommonUtils';

export const fetchLoginUserAllInfo = async (
  loginInfo: LoginInfo
): Promise<LoginUserAllInfo> => {
  const loginName = loginInfo.loginName;
  const tenant = loginInfo.tenant;
  const landscape = loginInfo.landscape;
  const tenantLandscape: TenantLandscape = {
    tenant,
    landscape,
  };
  const tenantLandscapeLoginName = getSHA3of(
    `${tenant}#${landscape}#${loginName}`
  );
  const accessedTimeStamp: ModelIntKeyConditionInput = {
    gt: 0,
  };
  const favoriteConditionInput: ModelFavoriteUserFavoritesCompositeKeyConditionInput = {
    eq: tenantLandscape,
  };
  const displayStartDate: ModelIntKeyConditionInput = {
    lt: getTimeOfTomorrow(),
  };
  const variables: LoginUserAllInfoQueryVariables = {
    loginName,
    tenant,
    landscape,
    favoriteTenantLandscape: favoriteConditionInput,
    tenantLandscapeLoginName,
    accessedTimeStamp,
    displayStartDate,
  };
  const result = await graphqlQuery<
    LoginUserAllInfoQuery,
    LoginUserAllInfoQueryVariables
  >(getLoginUserAllInfo, variables);

  const userInfo = loginUserInfo(result);
  const menusQueryResult = await getMenusQueryResult(loginInfo, result);

  return {
    userInfo,
    menusQueryResult,
    cwsNotificationHtml: cwsNotificationHtml(result),
    cwsEventHtml: cwsEventHtml(result),
    userAnnouncements: userAnnouncementsByDisplayStartDate(result),
  };
};

export const fetchLoginUserAllInfoWithoutAnnouncement = async (
  loginInfo: LoginInfo
) => {
  const loginName = loginInfo.loginName;
  const tenant = loginInfo.tenant;
  const landscape = loginInfo.landscape;
  const tenantLandscape: TenantLandscape = {
    tenant,
    landscape,
  };
  const tenantLandscapeLoginName = getSHA3of(
    `${tenant}#${landscape}#${loginName}`
  );
  const accessedTimeStamp: ModelIntKeyConditionInput = {
    gt: 0,
  };
  const favoriteConditionInput: ModelFavoriteUserFavoritesCompositeKeyConditionInput = {
    eq: tenantLandscape,
  };
  const variables: LoginUserAllInfoWithoutAnnouncementQueryVariables = {
    loginName,
    tenant,
    landscape,
    favoriteTenantLandscape: favoriteConditionInput,
    tenantLandscapeLoginName,
    accessedTimeStamp,
  };
  const result = await graphqlQuery<
    LoginUserAllInfoWithoutAnnouncementQuery,
    LoginUserAllInfoWithoutAnnouncementQueryVariables
  >(getLoginUserAllInfoWithoutAnnouncement, variables);

  const userInfo = loginUserInfo(result);
  const menusQueryResult = await getMenusQueryResult(loginInfo, result);

  return {
    userInfo,
    menusQueryResult,
    cwsNotificationHtml: null,
    cwsEventHtml: null,
    userAnnouncements: [],
  };
};

export const fetchLoginUserAllInfoWithoutCWS = async (loginInfo: LoginInfo) => {
  const loginName = loginInfo.loginName;
  const tenant = loginInfo.tenant;
  const landscape = loginInfo.landscape;
  const tenantLandscape: TenantLandscape = {
    tenant,
    landscape,
  };
  const tenantLandscapeLoginName = getSHA3of(
    `${tenant}#${landscape}#${loginName}`
  );
  const accessedTimeStamp: ModelIntKeyConditionInput = {
    gt: 0,
  };
  const favoriteConditionInput: ModelFavoriteUserFavoritesCompositeKeyConditionInput = {
    eq: tenantLandscape,
  };
  const displayStartDate: ModelIntKeyConditionInput = {
    lt: getTimeOfTomorrow(),
  };
  const variables: LoginUserAllInfoQueryVariables = {
    loginName,
    tenant,
    landscape,
    favoriteTenantLandscape: favoriteConditionInput,
    tenantLandscapeLoginName,
    accessedTimeStamp,
    displayStartDate,
  };
  const result = await graphqlQuery<
    LoginUserAllInfoWithoutCWSQuery,
    LoginUserAllInfoQueryVariables
  >(getLoginUserAllInfoWithoutCWS, variables);

  const userInfo = loginUserInfo(result);
  const menusQueryResult = await getMenusQueryResult(loginInfo, result);

  return {
    userInfo,
    menusQueryResult,
    cwsNotificationHtml: null,
    cwsEventHtml: null,
    userAnnouncements: userAnnouncementsByDisplayStartDate(result),
  };
};

export const fetchLoginUserAllInfoWithAnnouncementSetting = async (
  loginInfo: LoginInfo,
  announcementSetting: AnnouncementSetting
) => {
  if (announcementSetting === AnnouncementSetting.UseV2) {
    return await fetchLoginUserAllInfoWithoutCWS(loginInfo);
  }
  if (announcementSetting === AnnouncementSetting.NotUse) {
    return await fetchLoginUserAllInfoWithoutAnnouncement(loginInfo);
  }
  return await fetchLoginUserAllInfo(loginInfo);
};

const loginUserInfo = (
  loginUserAllInfoQuery:
    | LoginUserAllInfoQuery
    | LoginUserAllInfoWithoutAnnouncementQuery
    | null
): UserInfo => {
  if (loginUserAllInfoQuery && loginUserAllInfoQuery.loginUser) {
    const loginUser = loginUserAllInfoQuery.loginUser;
    return {
      isAdmin: _BooleanEnum2boolean(loginUser.isAdmin),
      language: loginUser.language,
      userName: loginUser.name,
      affiliationName: loginUser.affiliation,
      useFavoritePanel: _BooleanEnum2boolean(loginUser.displaysFavoritePanel),
      canUseCsr: !!loginUser.csrSchema,
      canUseCws: !!loginUser.cwsSchema,
      canUseCjk: !!loginUser.cjkSchema,
      canUseCtm: !!loginUser.ctmSchema,
    };
  } else {
    return {
      isAdmin: false,
      language: 'ja',
      userName: 'No Name',
      affiliationName: 'No Data',
      useFavoritePanel: false,
      canUseCsr: false,
      canUseCws: false,
      canUseCjk: false,
      canUseCtm: false,
    };
  }
};

const getMenusQueryResult = async (
  loginInfo: LoginInfo,
  loginUserAllInfoQuery:
    | LoginUserAllInfoQuery
    | LoginUserAllInfoWithoutAnnouncementQuery
    | null
): Promise<GetMenusQuery | null> => {
  const favoriteMenuIdList: ID[] = favoriteMenuIds(loginUserAllInfoQuery);
  favoriteListModule.setUp(favoriteMenuIdList);
  const historyMenuIdList: ID[] = historyMenuIds(loginUserAllInfoQuery);
  if (favoriteMenuIdList.length || historyMenuIdList.length) {
    const variables: GetMenusQueryVariables = { ...loginInfo };
    const favoriteCategoryIds = {
      category: 'favorite',
      ids: favoriteMenuIdList,
    };
    const historyCategoryIds = {
      category: 'history',
      ids: historyMenuIdList,
    };
    const categoryIdsCollection: CategoryIdsCollection = [
      favoriteCategoryIds,
      historyCategoryIds,
    ];
    const result = await graphqlQuery<GetMenusQuery, GetMenusQueryVariables>(
      getMenusByIds(categoryIdsCollection),
      variables
    );
    return result;
  }
  return null;
};

const favoriteMenuIds = (
  loginUserAllInfoQuery:
    | LoginUserAllInfoQuery
    | LoginUserAllInfoWithoutAnnouncementQuery
    | null
): ID[] => {
  return loginUserAllInfoQuery &&
    loginUserAllInfoQuery.favoriteMenus &&
    loginUserAllInfoQuery.favoriteMenus.items
    ? loginUserAllInfoQuery.favoriteMenus.items
        .map(
          (item: FavoriteType | null): FavoriteType =>
            item
              ? { menuId: item.menuId, sort: item.sort }
              : { menuId: '', sort: -1 }
        )
        .sort(
          (item1: FavoriteType, item2: FavoriteType) => item1.sort - item2.sort
        )
        .filter((item: FavoriteType) => item.menuId.length)
        .map((item: FavoriteType) => item.menuId)
    : [];
};

const historyMenuIds = (
  loginUserAllInfoQuery:
    | LoginUserAllInfoQuery
    | LoginUserAllInfoWithoutAnnouncementQuery
    | null
): ID[] => {
  if (
    loginUserAllInfoQuery &&
    loginUserAllInfoQuery.historyMenus &&
    loginUserAllInfoQuery.historyMenus.items
  ) {
    const menuIds: ID[] = [];
    for (let value of Object.values(loginUserAllInfoQuery.historyMenus.items)) {
      menuIds.push(value.menuId);
    }
    return menuIds;
  }
  return [];
};

export const editMenus = (
  getMenusQueryResult: GetMenusQuery,
  productAdditionalQueryMap: ProductAdditionalQueryMap,
  language: Language,
  isInHouseIPCWS: boolean,
  isInHouseIPCSR: boolean,
  disableDueToNotInHouseDescription: string
): ProductMenuMap => {
  const productMenuMap: ProductMenuMap = {
    favoriteMenuList: [],
    historyMenuList: [],
  };
  if (getMenusQueryResult) {
    for (var key in getMenusQueryResult) {
      const menu: MenuType | null = getMenusQueryResult[key];
      if (menu) {
        const title = getNameByLang(menu.menuNames.items, language);
        const description = getDescriptionByLang(
          menu.menuDescriptions.items,
          language
        );
        const product = menu.product;
        const url = addParameterToUrl(
          menu.url,
          product,
          productAdditionalQueryMap
        );
        const canUseFromExtra = _BooleanEnum2boolean(menu.canUseFromExtra);
        const available = isAvailable(
          product,
          canUseFromExtra,
          isInHouseIPCWS,
          isInHouseIPCSR
        );
        const editedDescription = available
          ? description
          : disableDueToNotInHouseDescription;
        key.startsWith('favorite')
          ? productMenuMap.favoriteMenuList.push({
              id: menu.menuId,
              title,
              menuType: 'menu',
              product,
              url,
              description: editedDescription,
              available,
              isFavorite: true,
              icon: getIcon(menu.menuIconFont),
              canUseFromExtra,
            })
          : productMenuMap.historyMenuList.push({
              id: menu.menuId,
              title,
              menuType: 'menu',
              product,
              url,
              description: editedDescription,
              available,
              isFavorite: true,
              icon: getIcon(menu.menuIconFont),
              canUseFromExtra,
            });
      }
    }
  }
  return productMenuMap;
};

export const isAvailable = (
  product: Product,
  canUseFromExtra: boolean,
  isInHouseIPCWS: boolean,
  isInHouseIPCSR: boolean
): boolean => {
  if (product === Product.CWS) {
    if (!isInHouseIPCWS && !canUseFromExtra) {
      return false;
    }
  } else if (product === Product.CSR) {
    if (!isInHouseIPCSR && !canUseFromExtra) {
      return false;
    }
  }
  return true;
};

const cwsNotificationHtml = (
  loginUserAllInfoQuery: LoginUserAllInfoQuery | null
): string | null => {
  return loginUserAllInfoQuery && loginUserAllInfoQuery.cwsNotification
    ? loginUserAllInfoQuery.cwsNotification.html
    : null;
};

const cwsEventHtml = (
  loginUserAllInfoQuery: LoginUserAllInfoQuery | null
): string | null => {
  return loginUserAllInfoQuery && loginUserAllInfoQuery.cwsEvent
    ? loginUserAllInfoQuery.cwsEvent.html
    : null;
};

const userAnnouncementsByDisplayStartDate = (
  loginUserAllInfoQuery:
    | LoginUserAllInfoQuery
    | LoginUserAllInfoWithoutCWSQuery
    | null
): UserAnnouncement[] => {
  if (
    loginUserAllInfoQuery &&
    loginUserAllInfoQuery.userAnnouncements &&
    loginUserAllInfoQuery.userAnnouncements.items
  ) {
    return loginUserAllInfoQuery.userAnnouncements.items
      .filter(
        (userAnnouncement: UserAnnouncementType) =>
          _BooleanEnum2boolean(userAnnouncement.isShow) &&
          (userAnnouncement.displayEndDate >= getTimeOfToday() ||
            userAnnouncement.displayEndDate === 0)
      )
      .map(
        (userAnnouncement: UserAnnouncementType): UserAnnouncement => {
          return convertUserAnnouncement(userAnnouncement);
        }
      );
  }
  return [];
};
